import { Controller } from "@hotwired/stimulus";

// Case-note comment form chrome: textarea autosize, submit enable/disable,
// tag chip rendering, and closing the tag <details> on outside click.
// Persistence lives in the companion form-draft controller — this renders
// and never touches localStorage.
export default class extends Controller<HTMLFormElement> {
  static targets = ["input", "submit", "checkbox", "selected"];

  declare inputTarget: HTMLTextAreaElement;
  declare hasInputTarget: boolean;
  declare submitTargets: HTMLButtonElement[];
  declare checkboxTargets: HTMLInputElement[];
  declare hasCheckboxTarget: boolean;
  declare selectedTarget: HTMLElement;
  declare hasSelectedTarget: boolean;

  private boundCloseDetails = this.closeDetailsOnClickOutside.bind(this);

  connect() {
    this.updateSubmitState();
    this.renderTags();
    this.adjustHeight();
    document.addEventListener("click", this.boundCloseDetails);
  }

  disconnect() {
    document.removeEventListener("click", this.boundCloseDetails);
  }

  input() {
    this.adjustHeight();
    this.updateSubmitState();
  }

  toggleTag(event: Event) {
    const input = (event.target as HTMLElement)
      .closest("label")
      ?.querySelector("input[type=checkbox]") as HTMLInputElement;
    if (input) {
      this.renderTags();
      const details = input.closest("details");
      if (details) details.removeAttribute("open");
    }
  }

  removeTag(event: Event) {
    const button = (event.target as HTMLElement).closest(
      "[data-tag-id]"
    ) as HTMLElement;
    if (!button) return;

    const tagId = button.dataset.tagId;
    const checkbox = this.checkboxTargets.find((cb) => cb.value === tagId);
    if (checkbox) {
      checkbox.checked = false;
      // Bubbling change reaches the label's actions, so the chip re-render
      // here and form-draft's save both fire exactly as a real click would.
      checkbox.dispatchEvent(new Event("change", { bubbles: true }));
      this.renderTags();
    }
  }

  adjustHeight() {
    if (!this.hasInputTarget) return;

    this.inputTarget.style.height = "auto";
    this.inputTarget.style.height = `${this.inputTarget.scrollHeight}px`;
  }

  updateSubmitState() {
    const isEmpty = !this.hasInputTarget || this.inputTarget.value.length === 0;
    const dictationBusy = this.element.dataset.speechDictationBusy === "true";
    this.submitTargets.forEach((btn) => {
      btn.disabled = isEmpty || dictationBusy;
    });
  }

  renderTags() {
    if (!this.hasSelectedTarget) return;

    const selected = this.checkboxTargets.filter((cb) => cb.checked);
    this.selectedTarget.replaceChildren(
      ...selected.map((cb) => {
        const tag = document.createElement("div");
        tag.className = "interaction-tag";

        const text = document.createTextNode(cb.dataset.tagName || "");
        tag.appendChild(text);

        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "ml-0.5 hover:text-gray-700";
        btn.dataset.tagId = cb.value;
        btn.dataset.action = "case-note-form#removeTag";
        btn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" class="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>`;
        tag.appendChild(btn);

        return tag;
      })
    );
  }

  closeDetailsOnClickOutside(event: Event) {
    const target = event.target as HTMLElement;
    const details = this.element.querySelector("details[open]");
    if (details && !details.contains(target)) {
      details.removeAttribute("open");
    }
  }
}
